import { useEffect, useRef, useState } from 'react'

export type PaginatedTableMeta = {
  filters: Record<string, any>
  page: number
  rowsPerPage: number
  sortBy: string
  // '' = unsorted initial state (some tables start without a default sort).
  sortOrder: 'asc' | 'desc' | ''
}

const STATE: PaginatedTableMeta = {
  filters: {},
  page: 0,
  rowsPerPage: 25,
  sortBy: 'created_at',
  sortOrder: 'desc',
}

export type TableMetaPersistKey =
  | 'campaigns'
  | 'campaignsReview'
  | 'companies'
  | 'customers'
  | 'dataChangeRequests'
  | 'users'

const metaCache = new Map<TableMetaPersistKey, PaginatedTableMeta>()

export const clearPaginatedTableMetaCache = () => metaCache.clear()

export const usePaginatedTableMeta = (
  initialState: Partial<PaginatedTableMeta> = {},
  persistKey?: TableMetaPersistKey,
) => {
  const [state, setState] = useState<PaginatedTableMeta>(
    () => (persistKey && metaCache.get(persistKey)) || { ...STATE, ...initialState },
  )

  useEffect(() => {
    if (persistKey) metaCache.set(persistKey, state)
  }, [persistKey, state])

  const setFilters = (filters: Record<string, any>) => setState(s => ({ ...s, filters }))
  const setPage = (page: number) => setState(s => ({ ...s, page }))
  const setRowsPerPage = (rowsPerPage: number) => setState(s => ({ ...s, rowsPerPage }))
  const setSort = (field: string) =>
    setState(s => ({
      ...s,
      sortBy: field,
      sortOrder: s.sortBy === field && s.sortOrder === 'asc' ? 'desc' : 'asc',
    }))

  const isFirstRun = useRef(true)
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false
      return
    }
    setPage(0)
  }, [state.rowsPerPage, state.sortBy, state.sortOrder, state.filters])

  return {
    meta: state,
    setPage,
    setRowsPerPage,
    setSort,
    setFilters,
  }
}
